import React, {useEffect, useState} from "react";
import api from "../api/axiosInstance";
import ProductFormModal from "../components/ProductFormModal";

const PrintifyProducts = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        category: "",
        subcategory: "",
        price: "",
        quantity: "",
        alttext: "",
        details: "",
        tags: "",
        supplier: "Printify",
        isFeatured: false,
        newProduct: true,
        variants: {},
    });

    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        try {
            const res = await api.get("/admin/products/printify");
            setProducts(res.data.data || []);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    };

    const getVariantKey = (v, idx) => v.id ?? v.sku ?? String(idx);

    const getVariantLabel = (v) => {
        if (v.title) return v.title;
        if (Array.isArray(v.options) && v.options.length) {
            return v.options.map((o) => (o.value ?? o)).join(" / ");
        }
        if (Array.isArray(v.option_values) && v.option_values.length) {
            return v.option_values.map((o) => (o.value ?? o)).join(" / ");
        }
        return "Variant";
    };

    const getVariantAttributes = (variant, colorMap, sizeMap) => {
        const attributes = {};

        (variant.options ?? []).forEach(optionId => {
            if (colorMap?.[optionId]) {
                attributes.color = colorMap[optionId];
            }

            if (sizeMap?.[optionId]) {
                attributes.size = sizeMap[optionId];
            }
        });

        return attributes;
    };

    const openAddModal = (product) => {
        const variantDefaults = {};
        product.variants.forEach((v, idx) => {
            const key = getVariantKey(v, idx);
            variantDefaults[key] = true;
        });
        setSelectedProduct(product);
        setFormData({
            category: "",
            subcategory: "",
            price: (() => {
                const first = product.variants?.[0];
                const p = first?.price?.amount ?? first?.price ?? "";
                if (typeof p === "number") return p > 1000 ? (p / 100).toFixed(2) : p;
                return "";
            })(),
            quantity: "",
            alttext: product.images?.[0]?.alt ?? product.title ?? "",
            details: (Array.isArray(product.details) ? product.details.join("\n") : ""),
            tags: (Array.isArray(product.tags) ? product.tags.join(", ") : ""),
            supplier: "Printify",
            supplierId: product.id?.toString() || '',
            isFeatured: false,
            newProduct: false,
            variants: variantDefaults,
        });
        setShowModal(true);
    };

    const buildModalProduct = (product, formData) => {
        const productMockupImages = (product.images || []).map(img => img.src);
        const detailsArray = formData.details
            ? formData.details.split("\n").map(s => s.trim()).filter(Boolean)
            : (Array.isArray(product.details) ? product.details : []);
        const tagsArray = formData.tags
            ? formData.tags.split(",").map(s => s.trim()).filter(Boolean)
            : (Array.isArray(product.tags) ? product.tags : []);
        const buildOptionMap = (options) =>
            Object.fromEntries(
                (options ?? [])
                    .map(o => [o.id, o.title])
            );
        const colorMap = buildOptionMap(product?.options.find((x)=>x.name==='Colors')?.values);
        const sizeMap = buildOptionMap(product?.options.find((x)=>x.name==='Sizes')?.values);
        const variants = product.variants
            .filter(v => v.is_enabled && v.is_available)
            .map((v, idx) => {
                const key = v.id ?? v.sku ?? String(idx);
                const selected = !!formData.variants[key];
                return {
                    id: key,
                    sku: v.sku ?? v.id ?? "",
                    title: v.title ?? getVariantLabel(v),
                    attributes: getVariantAttributes(v, colorMap, sizeMap),
                    price: parseFloat(formData.price),
                    quantity: 100,
                    image: (product.images || [])
                        .filter(img => Array.isArray(img.variant_ids) && img.variant_ids.includes(v.id))
                        .map(img => img.src),
                    selected
                };
            });

        return {
            image: productMockupImages,
            alttext: formData.alttext || product.title || "",
            title: product.title,
            description: formData.alttext || product.title || "",
            details: detailsArray,
            isFeatured: !!formData.isFeatured,
            price: parseFloat(formData.price) || 0,
            sku: product.id,
            variants,
            supplier: formData.supplier || "Printify",
            quantity: parseInt(formData.quantity, 10) || 0,
            tags: tagsArray,
            category: formData.category || "Uncategorized",
            subcategory: formData.subcategory || "General",
            newProduct: !!formData.newProduct,
        };
    };

    const handleModalSubmit = async (data) => {
        try {
            await api.post("/admin/products", data);
            setShowModal(false);
            fetchProducts();
        } catch (err) {
            alert("Failed to add product: " + (err.response?.data?.error || err.message));
        }
    };

    if (loading) return <p className="p-4">Loading products…</p>;
    if (error) return <p className="p-4 text-red-600">Error: {error}</p>;

    return (
        <div className="p-6">
            <h1 className="text-2xl font-bold mb-6">Printify Products</h1>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {products.map((p) => {
                    const status = p.publishing_status || "unknown";
                    const isPublished = status === "succeeded";
                    return (
                        <div key={p.id} className="border rounded-lg shadow p-4 flex flex-col items-center">
                            {p.images?.[0]?.src && (
                                <img src={p.images[0].src} alt={p.title} className="w-40 h-40 object-contain mb-4"/>
                            )}
                            <h2 className="text-lg font-semibold text-center">{p.title}</h2>
                            <p className="text-gray-600 text-sm mb-2">{p.variants.length} variants</p>
                            <p className="text-xs text-gray-400 mb-2">ID: {p.id}</p>
                            <div className="flex gap-2">
                                <button
                                    className={`px-3 py-1 rounded text-white ${isPublished ? "bg-gray-400 cursor-not-allowed" : "bg-green-500 hover:bg-green-600"}`}
                                    disabled={isPublished}
                                    onClick={async () => {
                                        try {
                                            await api.post(`/admin/products/printify/${p.id}/publish`);
                                            fetchProducts();
                                        } catch (err) {
                                            alert("Failed to publish product: " + (err.response?.data?.error || err.message));
                                        }
                                    }}
                                >
                                    Edit on Printify
                                </button>
                                <button className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                                        onClick={() => openAddModal(p)}>
                                    Add to Storefront
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
            <ProductFormModal
                open={showModal}
                onClose={() => setShowModal(false)}
                product={selectedProduct ? (() => {
                    const built = buildModalProduct(selectedProduct, formData);
                    delete built.options;
                    return built;
                })() : null}
                onSubmit={handleModalSubmit}
            />
        </div>
    );
};

export default PrintifyProducts;
